import React, { useState, useEffect } from "react";
import { Button, Upload, Table, message, Card, Typography, Input, Modal, Form, Tag } from "antd";
import { UploadOutlined, InfoCircleOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../constants/Constants";

const { Title, Text } = Typography;
const { TextArea } = Input;

const HomePageManagement = () => {
  const userId = localStorage.getItem("userId");
  
  // Data States
  const [carouselImages, setCarouselImages] = useState([]);
  const [midBannerImages, setMidBannerImages] = useState([]);
  const [endBannerImages, setEndBannerImages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);      
  
  // UI States
  const [uploading, setUploading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); 

  // Modal State
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [testimonialForm] = Form.useForm();

  useEffect(() => {
    fetchCarouselImages();
    fetchMidBannerImages();
    fetchEndBannerImages();
    fetchTestimonials(); 
  }, []);

  /* ================= FETCH DATA ================= */

  const fetchCarouselImages = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/homepage/carousel/get-images`);
      const transformedData = response.data.map((item) => ({ key: item.id.toString(), id: item.id, url: item.imageUrl, file: null }));
      setCarouselImages(transformedData);
    } catch (error) { console.error(error); }
  };

  const fetchMidBannerImages = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/homepage/mid-banners/get-images`);
      const transformedData = response.data.map((item) => ({ key: item.id.toString(), id: item.id, url: item.imageUrl, file: null }));
      setMidBannerImages(transformedData);
    } catch (error) { console.error(error); }
  };

  const fetchEndBannerImages = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/homepage/end-banner/get-active`); 
      const transformedData = response.data.map((item) => ({
        key: item.id.toString(), id: item.id, url: item.imageUrl, text: item.bannerText, file: null,
      }));
      setEndBannerImages(transformedData);
    } catch (error) { console.error("Failed to fetch End Banner"); }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/homepage/testimonials/all`);
      setTestimonials(response.data);
    } catch (error) { console.error("Failed to fetch Testimonials"); }
  };

  /* ================= HANDLE INPUTS ================= */

  const handleFileChange = (file, index, type) => {
    const updateState = (list, setList) => {
        const newList = [...list];
        newList[index] = { ...newList[index], file: file }; 
        setList(newList);
    };

    if (type === "carousel") updateState(carouselImages, setCarouselImages);
    else if (type === "midbanner") updateState(midBannerImages, setMidBannerImages);
    else if (type === "endbanner") updateState(endBannerImages, setEndBannerImages);
    
    return false; // Prevent automatic upload
  };

  const handleTextChange = (e, index, type) => {
      if(type === 'endbanner'){
          const newList = [...endBannerImages];
          newList[index] = { ...newList[index], text: e.target.value };
          setEndBannerImages(newList);
      }
  }

  /* ================= UPLOAD ACTIONS ================= */

  const uploadSingleImage = async (index, type) => {
    let item, url, listName;
    
    if (type === "carousel") { item = carouselImages[index]; listName = "Carousel"; }
    else if (type === "midbanner") { item = midBannerImages[index]; listName = "Mid Banner"; }
    else if (type === "endbanner") { item = endBannerImages[index]; listName = "End Banner"; }

    if (!item.file) { 
        message.warning("Please select an image file first."); 
        return; 
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", item.file);
    if(item.text) formData.append("text", item.text);

    // Construct URL
    if (type === "carousel") url = item.url ? `${BASE_URL}/homepage/carousel/update-image/${item.id}` : `${BASE_URL}/homepage/carousel/upload-images`;
    else if (type === "midbanner") url = item.url ? `${BASE_URL}/homepage/mid-banners/update-image/${item.id}` : `${BASE_URL}/homepage/mid-banners/upload-images`;
    else if (type === "endbanner") url = `${BASE_URL}/homepage/end-banner/add`;

    try {
      await axios.post(url, formData, { 
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true 
      });

      message.success(`${listName} Updated Successfully!`);
      setRefreshKey(prev => prev + 1);

      // Reset file selection in UI and Refetch
      if (type === "carousel") await fetchCarouselImages();
      else if (type === "midbanner") await fetchMidBannerImages();
      else if (type === "endbanner") await fetchEndBannerImages();

    } catch (error) { 
        console.error(error);
        message.error(`Failed to upload ${listName}.`); 
    } finally {
        setUploading(false);
    }
  };

  /* ================= TESTIMONIAL ACTIONS ================= */

  const handleAddTestimonial = async (values) => {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("review", values.review);
      if(values.image && values.image.length > 0){
          formData.append("file", values.image[0].originFileObj);
      } else {
          message.error("Please select a customer image");
          return;
      }

      try {
          await axios.post(`${BASE_URL}/homepage/testimonials/add`, formData, { 
              headers: { "Content-Type": "multipart/form-data" },
              withCredentials: true
          });
          message.success("Testimonial added!");
          setIsModalVisible(false);
          testimonialForm.resetFields();
          fetchTestimonials();
      } catch (error) {
          message.error("Failed to add testimonial");
      }
  };

  const deleteTestimonial = async (id) => {
      try {
          await axios.delete(`${BASE_URL}/homepage/testimonials/delete/${id}`, { withCredentials: true });
          message.success("Testimonial deleted");
          fetchTestimonials();
      } catch (error) { message.error("Failed to delete"); }
  };

  /* ================= HELPERS ================= */

  const handleAddItem = (type) => {
    if (type === "carousel") setCarouselImages([...carouselImages, { key: Date.now().toString(), id: null, url: "", file: null }]);
    else if (type === "midbanner") setMidBannerImages([...midBannerImages, { key: Date.now().toString(), id: null, url: "", file: null }]);
    else if (type === "endbanner") setEndBannerImages([...endBannerImages, { key: Date.now().toString(), id: null, url: "", text: "", file: null }]);
  };

  const handleDeleteItem = async (index, type) => {
      const item = type === "carousel" ? carouselImages[index] : midBannerImages[index];
      if(item && item.id && type !== 'endbanner') {
         const url = type === 'carousel' ? `${BASE_URL}/homepage/carousel/delete-images/${item.id}` : `${BASE_URL}/homepage/mid-banners/delete-images/${item.id}`;
         await axios.delete(url);
      }
      
      if (type === "carousel") { const n = [...carouselImages]; n.splice(index, 1); setCarouselImages(n); }
      else if (type === "midbanner") { const n = [...midBannerImages]; n.splice(index, 1); setMidBannerImages(n); }
      else if (type === "endbanner") { const n = [...endBannerImages]; n.splice(index, 1); setEndBannerImages(n); }
  };

  /* ================= RENDER ================= */
  
  const renderImage = (url) => url ? 
    <a href={url} target="_blank" rel="noreferrer">
        <img src={`${url}?t=${refreshKey}`} alt="img" style={{ width: "100px", borderRadius: '5px', border: '1px solid #ddd' }} />
    </a> 
    : <Tag color="blue">New Slot</Tag>;

  const renderFile = (file) => file ? <Tag color="green">{file.name}</Tag> : <Text type="secondary" style={{fontSize: '12px'}}>No file chosen</Text>;

  // Common Column Definition
  const commonColumns = (type) => [
    { title: "Image", dataIndex: "url", key: "url", render: renderImage, width: '150px' },
    { title: "Selected File", dataIndex: "file", key: "file", render: renderFile },
    { title: "Action", key: "action", render: (_, record, index) => (
        <div style={{display:'flex', gap:'10px', alignItems: 'center'}}>
          <Upload beforeUpload={(file) => handleFileChange(file, index, type)} showUploadList={false} accept="image/*">
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
          <Button 
            type="primary" 
            loading={uploading} 
            disabled={!record.file} // DISABLE IF NO FILE SELECTED
            icon={<SaveOutlined />} 
            onClick={() => uploadSingleImage(index, type)}
          >
            Upload
          </Button>
          <Button danger icon={<DeleteOutlined/>} onClick={() => handleDeleteItem(index, type)}/>
        </div>
      ) 
    },
  ];

  // End Banner Columns
  const endBannerColumns = [
    { title: "Current Banner", dataIndex: "url", key: "url", render: renderImage, width: '150px' },
    { title: "Banner Text (Optional)", dataIndex: "text", key: "text", render: (text, record, index) => (
        <Input placeholder="Enter quote or text" value={text} onChange={(e) => handleTextChange(e, index, 'endbanner')} />
    )},
    { title: "New File", dataIndex: "file", key: "file", render: renderFile },
    { title: "Action", key: "action", render: (_, record, index) => (
        <div style={{display:'flex', gap:'10px', alignItems: 'center'}}>
          <Upload beforeUpload={(file) => handleFileChange(file, index, "endbanner")} showUploadList={false} accept="image/*">
            <Button icon={<UploadOutlined />}>Select New Image</Button>
          </Upload>
          <Button 
            type="primary" 
            loading={uploading} 
            disabled={!record.file} // DISABLE IF NO FILE SELECTED
            icon={<SaveOutlined />} 
            onClick={() => uploadSingleImage(index, "endbanner")}
          >
             {record.url ? "Update Banner" : "Save Banner"}
          </Button>
        </div>
      ) 
    },
  ];

  const testimonialColumns = [
      { title: "Photo", dataIndex: "customerImageUrl", key: "img", render: renderImage },
      { title: "Name", dataIndex: "customerName", key: "name", width: '150px' },
      { title: "Review", dataIndex: "reviewText", key: "review", ellipsis: true },
      { title: "Action", key: "action", render: (_, record) => (
          <Button danger icon={<DeleteOutlined/>} onClick={() => deleteTestimonial(record.id)}>Delete</Button>
      )}
  ];

  return userId ? (
    <div style={{ padding: "20px" }}>
      
      {/* --- INSTRUCTIONS SECTION --- */}
      <Card style={{ marginBottom: "30px", backgroundColor: "#f0f5ff", border: "1px solid #adc6ff" }}>
        <div style={{ display: "flex", alignItems: "start", gap: "15px" }}>
          <InfoCircleOutlined style={{ fontSize: "24px", color: "#1890ff", marginTop: "5px" }} />
          <div>
            <Title level={4} style={{ marginTop: 0 }}>Instructions & Guidelines</Title>
            <ul style={{ paddingLeft: "20px", color: "#555" }}>
              <li><strong>Step 1:</strong> Click <Text code>+ Add Row</Text> (for Carousel/MidBanner) or <Text code>Select New Image</Text>.</li>
              <li><strong>Step 2:</strong> Choose your image file from your computer. The button will stay disabled until a file is picked.</li>
              <li><strong>Step 3:</strong> Click the <Text strong style={{color: '#1890ff'}}>Upload / Save</Text> button to publish changes.</li>
              <li><strong>Image Sizes:</strong> 
                <ul>
                    <li>Carousel: 1920x700 px</li>
                    <li>Mid/End Banner: 1920x400 px</li>
                    <li>Testimonials: Square images work best.</li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </Card>

      {/* 1. CAROUSEL */}
      <div style={{ marginBottom: "40px" }}>
        <Title level={3}>1. Main Carousel (Top)</Title>
        <div style={{ marginBottom: "10px", display: "flex", gap: 10 }}>
           <Button onClick={() => handleAddItem("carousel")}>+ Add Row</Button>
        </div>
        <Table columns={commonColumns("carousel")} dataSource={carouselImages} pagination={false} bordered />
      </div>

      {/* 2. MID BANNER */}
      <div style={{ marginBottom: "40px" }}>
        <Title level={3}>2. Mid Banner (Middle)</Title>
        <div style={{ marginBottom: "10px" }}><Button onClick={() => handleAddItem("midbanner")}>+ Add Row</Button></div>
        <Table columns={commonColumns("midbanner")} dataSource={midBannerImages} pagination={false} bordered />
      </div>

      {/* 3. END BANNER */}
      <div style={{ marginBottom: "40px" }}>
        <Title level={3}>3. End Banner (Quote Section)</Title>
        <Text type="secondary">This image replaces the background behind the quote/footer area.</Text>
        <div style={{ marginTop: "10px", marginBottom: "10px" }}>
             {endBannerImages.length === 0 && <Button onClick={() => handleAddItem("endbanner")}>+ Set Banner</Button>}
        </div>
        <Table columns={endBannerColumns} dataSource={endBannerImages} pagination={false} bordered />
      </div>

      {/* 4. TESTIMONIALS */}
      <div style={{ marginBottom: "40px" }}>
        <div style={{display:'flex', justifyContent:'space-between'}}>
            <Title level={3}>4. Customer Testimonials</Title>
            <Button type="primary" icon={<PlusOutlined/>} onClick={() => setIsModalVisible(true)}>Add Testimonial</Button>
        </div>
        <Table columns={testimonialColumns} dataSource={testimonials} pagination={false} bordered rowKey="id" />
      </div>

      {/* MODAL */}
      <Modal 
        title="Add New Testimonial" 
        open={isModalVisible} 
        visible={isModalVisible} 
        onCancel={() => setIsModalVisible(false)} 
        footer={null}
      >
          <Form form={testimonialForm} layout="vertical" onFinish={handleAddTestimonial}>
              <Form.Item name="name" label="Customer Name" rules={[{ required: true }]}>
                  <Input placeholder="e.g. John Doe" />
              </Form.Item>
              <Form.Item name="review" label="Review Text" rules={[{ required: true }]}>
                  <TextArea rows={4} placeholder="e.g. The products are amazing..." />
              </Form.Item>
              <Form.Item name="image" label="Customer Photo" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e && e.fileList} rules={[{ required: true, message: 'Please upload an image' }]}>
                  <Upload name="logo" listType="picture" maxCount={1} beforeUpload={() => false}>
                      <Button icon={<UploadOutlined />}>Click to Upload</Button>
                  </Upload>
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={uploading} block>Submit Testimonial</Button>
          </Form>
      </Modal>

    </div>
  ) : (
    <div style={{ textAlign: 'center', marginTop: '100px' }}>
        <h2>Please Sign In</h2>
    </div>
  );
};

export default HomePageManagement;